import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { Thumb } from '../components/ui/Avatar'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { ChannelDot } from '../components/ui/Channel'
import { channelDisplayName } from '../components/ui/channelDisplay'
import { Icon, I } from '../components/ui/icons'
import { MOBILE_SINGLE_COLUMN, TEXT_WRAP_STYLE, mobileButtonStyle } from '../components/ui'
import { Pill } from '../components/ui/Pill'
import { listChannelConnections, pushChannelListingStock, pushChannelProductStock, refreshNoonListing, refreshChannelListingStock } from '../channels/channelsApi'
import type { ChannelConnection, ChannelPushStockItemResult, ChannelPushStockResult, ChannelStockPushCapability } from '../channels/types'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { listInventoryConnections } from '../inventory/inventoryApi'
import type { InventoryConnection, InventorySingleItemImportResponse } from '../inventory/types'
import { ProductOptionsPanel } from '../products/ProductOptionsPanel'
import { deactivateProductAfterOptionsReleased, dismissProductOptionsReleased, getProduct, refreshProductFromZoho } from '../products/productsApi'
import type { Product, ProductChannelListing, ProductChannelListingStock, ProductImage, ProductStatus } from '../products/types'
import { getSourceAuthority } from '../source/sourceApi'
import type { SourceAuthority } from '../source/types'
import { getProductStock, updateProductStock } from '../stock/stockApi'
import type { ProductStock, ProductStockRow } from '../stock/types'

type StatusTone = 'success' | 'warning' | 'neutral'

type ProductDetailTab =
  | { kind: 'master'; id: 'master'; label: string; count: 0 }
  | { kind: 'channel'; id: string; label: string; providerName: string; channelConnectionId: number; count: number }

function statusTone(status: ProductStatus): StatusTone {
  if (status === 'ACTIVE') return 'success'
  if (status === 'DRAFT') return 'warning'
  return 'neutral'
}

function formatMoney(value: number | null | undefined, locale: string): string {
  const amount = Number(value ?? 0)
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(amount)
}

function formatDate(value: string, locale: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function formatOptionalDate(value: string | null | undefined, locale: string, emptyValue: string): string {
  if (!value) return emptyValue
  const formatted = formatDate(value, locale)
  return formatted === '—' ? emptyValue : formatted
}

function nonBlank(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

function providerQuantity(row: ProductChannelListingStock, locale: string, infiniteValue: string, emptyValue: string): string {
  if (row.infinite) return infiniteValue
  if (row.quantity == null) return emptyValue
  return row.quantity.toLocaleString(locale)
}

function errorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.products.errors.codes.${error.code}`
  }
  return 'pages.products.errors.codes.REQUEST_FAILED'
}

function stockErrorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.productDetail.stock.errors.${error.code}`
  }
  return 'pages.productDetail.stock.errors.generic'
}

function channelStockPushErrorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.productDetail.channel.stockPush.errors.${error.code}`
  }
  return 'pages.productDetail.channel.stockPush.errors.REQUEST_FAILED'
}

function channelStockPushResultItem(result: ChannelPushStockResult): ChannelPushStockItemResult | null {
  return result.items?.[0] ?? null
}

function localAvailableQuantity(stock: ProductStock | null): number {
  return stock?.rows.reduce((sum, row) => sum + row.availableQty, 0) ?? 0
}

function sallaPushableBranchRows(listing: ProductChannelListing): ProductChannelListingStock[] {
  return (listing.stocks ?? []).filter(row => {
    const locationId = nonBlank(row.externalLocationId)
    return locationId !== null && locationId.toLowerCase() !== 'default'
  })
}

function zohoRefreshErrorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.productDetail.zohoRefresh.errors.${error.code}`
  }
  return 'pages.productDetail.zohoRefresh.errors.REQUEST_FAILED'
}

function FieldRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  const isMobile = useIsMobileViewport()

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '150px 1fr', gap: isMobile ? 4 : 0, padding: '10px 0', borderTop: '1px solid var(--border-subtle)', fontSize: 13 }}>
      <div style={{ color: 'var(--fg-subtle)', fontWeight: 600 }}>{label}</div>
      <div style={{ color: 'var(--fg)', fontFamily: mono ? 'var(--font-mono)' : 'inherit', fontSize: mono ? 12 : 13, fontWeight: mono ? 600 : 400, minWidth: 0, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  )
}

function ProductImageTile({ image, productName }: { image: ProductImage; productName: string }) {
  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, overflow: 'hidden', background: 'white' }}>
      {image.publicUrl
        ? <img src={image.publicUrl} alt={image.altText ?? productName} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
        : <div style={{ width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--ink-50)' }}><Thumb name={productName} size={54} /></div>}
    </div>
  )
}

interface ProductStockPanelProps {
  accessToken: string
  productId: number
  stockSource: SourceAuthority | null
}

function ProductStockPanel({ accessToken, productId, stockSource }: ProductStockPanelProps) {
  const { t, i18n } = useTranslation()
  const editable = stockSource?.ownerType === 'TLAAQ'
  const isMobile = useIsMobileViewport()
  const [stock, setStock] = useState<ProductStock | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<unknown>(null)
  const [editingWarehouseId, setEditingWarehouseId] = useState<number | null>(null)
  const [draftQty, setDraftQty] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [rowError, setRowError] = useState<unknown>(null)
  const [reloadCounter, setReloadCounter] = useState(0)

  useEffect(() => {
    let cancelled = false
    // Intentional reset on deps change so the panel shows a loading state while
    // re-fetching after navigating between products. The rule's general guidance
    // doesn't fit this canonical fetch-on-deps pattern.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setLoadError(null)

    getProductStock(accessToken, productId)
      .then(response => {
        if (!cancelled) setStock(response)
      })
      .catch(caught => {
        if (!cancelled) setLoadError(caught)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [accessToken, productId, reloadCounter])

  const beginEdit = (row: ProductStockRow) => {
    if (!editable) return
    setEditingWarehouseId(row.warehouseId)
    setDraftQty(String(row.onHandQty))
    setRowError(null)
  }

  const cancelEdit = () => {
    setEditingWarehouseId(null)
    setDraftQty('')
    setRowError(null)
  }

  const saveEdit = async (row: ProductStockRow) => {
    if (!editable) return
    const trimmed = draftQty.trim()
    if (trimmed === '' || !/^-?\d+$/.test(trimmed)) {
      setRowError(new ApiClientError(400, { code: 'PRODUCT_STOCK_INVALID_QUANTITY' }))
      return
    }
    const onHandQty = Number(trimmed)
    if (!Number.isFinite(onHandQty) || onHandQty < 0) {
      setRowError(new ApiClientError(400, { code: 'PRODUCT_STOCK_INVALID_QUANTITY' }))
      return
    }

    setSaving(true)
    setRowError(null)
    try {
      const updated = await updateProductStock(accessToken, productId, row.warehouseId, {
        onHandQty,
        version: row.version,
      })
      setStock(prev => {
        if (!prev) return prev
        return {
          ...prev,
          rows: prev.rows.map(r => (r.warehouseId === updated.warehouseId ? updated : r)),
        }
      })
      setEditingWarehouseId(null)
      setDraftQty('')
    } catch (caught) {
      setRowError(caught)
      if (caught instanceof ApiClientError && caught.code === 'PRODUCT_STOCK_VERSION_CONFLICT') {
        setReloadCounter(value => value + 1)
        setEditingWarehouseId(null)
        setDraftQty('')
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{ marginTop: 28 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-subtle)', marginBottom: 10 }}>{t('pages.productDetail.stock.title')}</div>
        <div style={{ padding: '14px 16px', borderRadius: 10, background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon size={16}>{I.refresh}</Icon>
          {t('pages.productDetail.stock.loading')}
        </div>
      </div>
    )
  }

  if (loadError !== null) {
    return (
      <div style={{ marginTop: 28 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-subtle)', marginBottom: 10 }}>{t('pages.productDetail.stock.title')}</div>
        <div style={{ padding: '14px 16px', borderRadius: 10, border: '1px solid var(--danger-100)', color: 'var(--danger)', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon size={16}>{I.alert}</Icon>
          {String(t(stockErrorMessageKey(loadError), { defaultValue: String(t('pages.productDetail.stock.errors.generic')) }))}
        </div>
      </div>
    )
  }

  const rows = stock?.rows ?? []
  const mobileActionStyle = mobileButtonStyle(isMobile)

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('pages.productDetail.stock.title')}</div>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 4 }}>
            {editable || !stockSource ? t('pages.productDetail.stock.subtitle') : t('pages.productDetail.stock.readOnly', { source: stockSource.provider ?? stockSource.ownerType })}
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 4 }}>{t('pages.productDetail.stock.channelHint')}</div>
        </div>
      </div>

      {rowError !== null && (
        <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 10, border: '1px solid var(--danger-100)', color: 'var(--danger)', fontSize: 12.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon size={15}>{I.alert}</Icon>
          {String(t(stockErrorMessageKey(rowError), { defaultValue: String(t('pages.productDetail.stock.errors.generic')) }))}
        </div>
      )}

      {rows.length === 0 ? (
        <div style={{ padding: '20px 16px', borderRadius: 10, background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 13, textAlign: 'center' }}>
          {t('pages.productDetail.stock.empty')}
        </div>
      ) : isMobile ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map(row => {
            const editing = editable && editingWarehouseId === row.warehouseId
            return (
              <div key={row.warehouseId} style={{ display: 'grid', gap: 12, padding: 14, borderRadius: 12, border: '1px solid var(--border-subtle)', background: 'white', minWidth: 0 }}>
                <div style={TEXT_WRAP_STYLE}>
                  <div style={{ fontWeight: 800, color: 'var(--fg)' }}>{row.warehouseName}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)', marginTop: 2, ...TEXT_WRAP_STYLE }}>{row.warehouseCode}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('pages.productDetail.stock.headers.onHand')}</div>
                    <div style={{ marginTop: 4, fontWeight: 800, color: 'var(--fg)' }}>
                      {editing ? (
                        <input
                          aria-label={String(t('pages.productDetail.stock.headers.onHand'))}
                          type="number"
                          min={0}
                          step={1}
                          value={draftQty}
                          onChange={event => setDraftQty(event.target.value)}
                          style={{
                            width: '100%', minWidth: 0, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit',
                            color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                            borderRadius: 7, outline: 'none', textAlign: 'end',
                          }}
                        />
                      ) : row.onHandQty.toLocaleString(i18n.language)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('pages.productDetail.stock.headers.committed')}</div>
                    <div style={{ marginTop: 4, color: 'var(--fg-muted)' }}>{row.committedQty.toLocaleString(i18n.language)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('pages.productDetail.stock.headers.available')}</div>
                    <div style={{ marginTop: 4, fontWeight: 800, color: 'var(--fg)' }}>{row.availableQty.toLocaleString(i18n.language)}</div>
                  </div>
                </div>
                {editing ? (
                  <div style={{ display: 'grid', gap: 8 }}>
                    <Button kind="primary" size="xs" onClick={() => saveEdit(row)} disabled={saving} style={mobileActionStyle}>
                      {t('pages.productDetail.stock.actions.save')}
                    </Button>
                    <Button kind="secondary" size="xs" onClick={cancelEdit} disabled={saving} style={mobileActionStyle}>
                      {t('pages.productDetail.stock.actions.cancel')}
                    </Button>
                  </div>
                ) : (
                  editable && <Button kind="secondary" size="xs" onClick={() => beginEdit(row)} style={mobileActionStyle}>
                    {t('pages.productDetail.stock.actions.edit')}
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div style={{ borderRadius: 10, border: '1px solid var(--border-subtle)', overflowX: 'auto', background: 'white' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--ink-50)' }}>
                {[
                  t('pages.productDetail.stock.headers.warehouse'),
                  t('pages.productDetail.stock.headers.onHand'),
                  t('pages.productDetail.stock.headers.committed'),
                  t('pages.productDetail.stock.headers.available'),
                  '',
                ].map((header, index) => (
                  <th key={index} style={{ textAlign: index === 0 ? 'start' : index === 4 ? 'end' : 'end', padding: '10px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const editing = editable && editingWarehouseId === row.warehouseId
                return (
                  <tr key={row.warehouseId} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 700, color: 'var(--fg)' }}>{row.warehouseName}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)' }}>{row.warehouseCode}</div>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'end', fontWeight: 700, color: 'var(--fg)' }}>
                      {editing ? (
                        <input
                          aria-label={String(t('pages.productDetail.stock.headers.onHand'))}
                          type="number"
                          min={0}
                          step={1}
                          value={draftQty}
                          onChange={event => setDraftQty(event.target.value)}
                          style={{
                            width: 96, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit',
                            color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                            borderRadius: 7, outline: 'none', textAlign: 'end',
                          }}
                        />
                      ) : row.onHandQty.toLocaleString(i18n.language)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'end', color: 'var(--fg-muted)' }}>
                      {row.committedQty.toLocaleString(i18n.language)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'end', fontWeight: 700, color: 'var(--fg)' }}>
                      {row.availableQty.toLocaleString(i18n.language)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'end' }}>
                      {editing ? (
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <Button kind="primary" size="xs" onClick={() => saveEdit(row)} disabled={saving}>
                            {t('pages.productDetail.stock.actions.save')}
                          </Button>
                          <Button kind="secondary" size="xs" onClick={cancelEdit} disabled={saving}>
                            {t('pages.productDetail.stock.actions.cancel')}
                          </Button>
                        </div>
                      ) : (
                        editable && <Button kind="secondary" size="xs" onClick={() => beginEdit(row)}>
                          {t('pages.productDetail.stock.actions.edit')}
                        </Button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function OptionsReleasedNotice({ product, accessToken, onProductChange }: { product: Product; accessToken: string; onProductChange: (product: Product) => void }) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const resolve = async (deactivate: boolean) => {
    setBusy(true)
    setError(null)
    try {
      onProductChange(deactivate
        ? await deactivateProductAfterOptionsReleased(accessToken, product.id)
        : await dismissProductOptionsReleased(accessToken, product.id))
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Card padding={0} style={{ marginBottom: 16 }}>
      <div role="status" style={{ display: 'flex', alignItems: isMobile ? 'stretch' : 'center', gap: 14, padding: '16px 20px', flexDirection: isMobile ? 'column' : 'row' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 10, background: 'var(--brand-100)', flexShrink: 0 }}>
          <Icon size={16} color="var(--brand-700)">{I.link}</Icon>
        </div>
        <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, lineHeight: '19px', color: 'var(--fg)' }}>
          {t('pages.products.options.released.message')}
          {error !== null && (
            <div style={{ marginTop: 4, color: 'var(--danger)', fontWeight: 700, fontSize: 12.5 }}>
              {String(t(errorMessageKey(error), { defaultValue: String(t('pages.products.errors.codes.REQUEST_FAILED')) }))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button kind="secondary" size="sm" disabled={busy} onClick={() => resolve(true)} style={mobileButtonStyle(isMobile)}>{t('pages.products.options.released.archive')}</Button>
          <Button kind="ghost" size="sm" disabled={busy} onClick={() => resolve(false)} style={mobileButtonStyle(isMobile)}>{t('pages.products.options.released.dismiss')}</Button>
        </div>
      </div>
    </Card>
  )
}

function MasterTab({ product, accessToken, stockSource, catalogSource }: { product: Product; accessToken: string; stockSource: SourceAuthority | null; catalogSource: SourceAuthority | null }) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const master = product.images.find(image => image.primary) ?? product.images[0]
  const gallery = product.images.filter(image => image.key !== master?.key)
  const hasOptions = (product.optionCount ?? 0) > 0
  return (
    <Card padding={isMobile ? 18 : 28} style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.35fr 1fr', gap: isMobile ? 20 : 28 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-subtle)', marginBottom: 14 }}>{t('pages.productDetail.master.title')}</div>
          <FieldRow label={t('pages.productDetail.master.fields.name')} value={product.name} />
          <FieldRow label={t('pages.productDetail.master.fields.description')} value={product.description || t('pages.productDetail.emptyValue')} />
          <FieldRow label={t('pages.productDetail.master.fields.sku')} mono={!product.generatedSku} value={product.generatedSku ? t(hasOptions ? 'pages.products.options.parentSkuNone' : 'pages.products.options.noSku') : product.sku} />
          <FieldRow label={t('pages.productDetail.master.fields.altSkus')} mono value={(product.altSkus ?? []).length === 0 ? t('pages.productDetail.emptyValue') : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {product.altSkus.map(sku => <span key={sku} style={{ padding: '4px 8px', borderRadius: 8, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)' }}>{sku}</span>)}
            </div>
          )} />
          <FieldRow label={t('pages.productDetail.master.fields.status')} value={<Pill tone={statusTone(product.status)} dot size="sm">{t(`pages.products.status.${product.status}`)}</Pill>} />
          <FieldRow label={t('pages.productDetail.master.fields.masterPrice')} value={t('pages.productDetail.price', { value: formatMoney(product.price, i18n.language) })} />
          <FieldRow label={t('pages.productDetail.master.fields.cost')} value={product.cost == null ? t('pages.productDetail.emptyValue') : t('pages.productDetail.price', { value: formatMoney(product.cost, i18n.language) })} />
          <FieldRow label={t('pages.productDetail.master.fields.salePrice')} value={product.salePrice == null ? t('pages.productDetail.emptyValue') : t('pages.productDetail.price', { value: formatMoney(product.salePrice, i18n.language) })} />
          <FieldRow label={t('pages.productDetail.master.fields.weight')} value={product.weightGrams == null ? t('pages.productDetail.emptyValue') : t('pages.productDetail.weightValue', { value: product.weightGrams })} />
          <FieldRow label={t('pages.productDetail.master.fields.taxRate')} value={product.taxRate == null ? t('pages.productDetail.emptyValue') : t('pages.productDetail.taxValue', { value: product.taxRate })} />
          <FieldRow label={t('pages.productDetail.master.fields.updated')} value={formatDate(product.updatedAt, i18n.language)} />

          <div style={{ marginTop: 24, padding: 14, background: 'var(--ink-50)', borderRadius: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
            <Icon size={16} color="var(--fg-subtle)">{I.shield}</Icon>
            <div style={{ flex: 1, fontSize: 12.5, color: 'var(--fg-muted)' }}>
              {t('pages.productDetail.master.phaseOneNote')}
            </div>
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-subtle)', marginBottom: 14 }}>{t('pages.productDetail.images.title')}</div>
          {master ? <ProductImageTile image={master} productName={product.name} /> : (
            <div style={{ padding: 28, borderRadius: 12, background: 'var(--ink-50)', textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
              {t('pages.productDetail.images.empty')}
            </div>
          )}
          {gallery.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(72px, 1fr))', gap: 8, marginTop: 10 }}>
              {gallery.map(image => <ProductImageTile key={image.key} image={image} productName={product.name} />)}
            </div>
          )}
        </div>
      </div>

      <FieldRow label={t('pages.productDetail.stock.source')} value={t('pages.productDetail.stock.ownership', { source: stockSource ? stockSource.provider ?? stockSource.ownerType : t('pages.productDetail.stock.sourceUnknown') })} />
      {hasOptions
        ? <ProductOptionsPanel accessToken={accessToken} productId={product.id} stockSource={stockSource} catalogSource={catalogSource} />
        : <ProductStockPanel accessToken={accessToken} productId={product.id} stockSource={stockSource} />}
    </Card>
  )
}

function StockObservation({ row }: { row: ProductChannelListingStock }) {
  const { t, i18n } = useTranslation()
  const emptyValue = t('pages.productDetail.emptyValue')
  const confirmed = !row.infinite && Number.isFinite(row.quantity) && row.quantity === row.submittedQuantity
    && Boolean(row.observedAt && row.submittedAt)
    && Date.parse(row.observedAt!) >= Date.parse(row.submittedAt!)
  return (
    <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--fg-muted)', display: 'grid', gap: 4, ...TEXT_WRAP_STYLE }}>
      <div style={{ fontSize: 'var(--fs-24)', fontWeight: 700, color: 'var(--fg)' }}>{row.submittedQuantity?.toLocaleString(i18n.language) ?? t('pages.productDetail.channel.notSubmitted')}</div>
      {row.submittedQuantity != null && <>
        <div>{t('pages.productDetail.channel.submittedAt')}: {formatOptionalDate(row.submittedAt, i18n.language, emptyValue)}</div>
        <div><Pill tone={confirmed ? 'success' : 'warning'} size="sm">{t(confirmed ? 'pages.productDetail.channel.confirmed' : 'pages.productDetail.channel.awaitingConfirmation')}</Pill></div>
        <div>{t(confirmed ? 'pages.productDetail.channel.confirmedBody' : 'pages.productDetail.channel.pendingBody')}</div>
      </>}
    </div>
  )
}

function ListingStockRows({ channel, listing, onRefresh, refreshing, renderBranchPush }: {
  channel: string
  listing: ProductChannelListing
  onRefresh?: () => Promise<void>
  refreshing: boolean
  renderBranchPush: (row: ProductChannelListingStock) => ReactNode
}) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const rows = listing.stocks ?? []
  const emptyValue = String(t('pages.productDetail.emptyValue'))

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-subtle)', marginBottom: 10 }}>
        {t('pages.productDetail.channel.stockRowsTitle')}
      </div>
      <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 10, ...TEXT_WRAP_STYLE }}>
        {listing.externalVariantId
          ? t('pages.productDetail.channel.variantStockScope', { variantId: listing.externalVariantId })
          : t('pages.productDetail.channel.listingStockScope')}
      </div>
      {isMobile && onRefresh && <Button kind="secondary" size="sm" icon={I.refresh} onClick={onRefresh} disabled={refreshing} style={{ ...mobileButtonStyle(true), marginBottom: 10 }}>
        {t(refreshing ? 'pages.productDetail.channel.noonRefreshing' : 'pages.productDetail.channel.noonRefresh')}
      </Button>}
      {rows.length === 0 ? (
        <div style={{ padding: '16px', borderRadius: 10, background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 13 }}>
          {t('pages.productDetail.channel.stockRowsEmpty', { channel })}
        </div>
      ) : isMobile ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map(row => {
            const locationName = nonBlank(row.externalLocationName) ?? nonBlank(row.externalLocationId) ?? emptyValue
            const locationId = nonBlank(row.externalLocationId) ?? emptyValue
            return (
              <div key={row.id ?? row.externalStockId} style={{ display: 'grid', gap: 'var(--sp-2)', padding: 'var(--sp-3)', borderRadius: 'var(--r-md)', border: '1px solid var(--border-subtle)', minWidth: 0 }}>
                <div style={TEXT_WRAP_STYLE}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('pages.productDetail.channel.branch')}</div>
                  <div style={{ marginTop: 4, fontWeight: 800, color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>{locationName}</div>
                  <div style={{ marginTop: 4, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-muted)', ...TEXT_WRAP_STYLE }}>{locationId}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{t('pages.productDetail.channel.observedIn', { channel })}</div>
                  <div style={{ marginTop: 4, fontSize: 'var(--fs-24)', fontWeight: 700, color: 'var(--fg)' }}>
                    {providerQuantity(row, i18n.language, t('pages.productDetail.channel.quantityInfinite'), t('pages.productDetail.channel.unknownQuantity'))}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 4 }}>
                    {row.observedAt ? `${t('pages.productDetail.channel.observedAt')}: ${formatOptionalDate(row.observedAt, i18n.language, emptyValue)}` : t('pages.productDetail.channel.notFetched')}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)', marginBottom: 4 }}>{t('pages.productDetail.channel.submittedTarget')}</div>
                  <StockObservation row={row} />
                </div>
                {renderBranchPush(row)}
              </div>
            )
          })}
        </div>
      ) : (
        <div style={{ borderRadius: 10, border: '1px solid var(--border-subtle)', overflowX: 'auto', background: 'white' }}>
          <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--ink-50)' }}>
                {[
                  t('pages.productDetail.channel.branch'),
                  t('pages.productDetail.channel.observedIn', { channel }),
                  t('pages.productDetail.channel.submittedTarget'),
                ].map((header, index) => (
                  <th key={index} style={{ width: index === 0 ? '30%' : '35%', textAlign: 'start', padding: 'var(--sp-4)', fontSize: 'var(--fs-11)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const locationName = nonBlank(row.externalLocationName) ?? nonBlank(row.externalLocationId) ?? emptyValue
                const locationId = nonBlank(row.externalLocationId) ?? emptyValue
                return (
                  <tr key={row.id ?? row.externalStockId} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: 'var(--sp-4)', verticalAlign: 'top', fontWeight: 700, color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>
                      {locationName}
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 400, color: 'var(--fg-muted)', marginTop: 4 }}>{locationId}</div>
                    </td>
                    <td style={{ padding: 'var(--sp-4)', verticalAlign: 'top', fontSize: 'var(--fs-24)', fontWeight: 700, color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>
                      {providerQuantity(row, i18n.language, t('pages.productDetail.channel.quantityInfinite'), t('pages.productDetail.channel.unknownQuantity'))}
                      <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--fg-muted)', marginTop: 4 }}>
                        {row.observedAt ? `${t('pages.productDetail.channel.observedAt')}: ${formatOptionalDate(row.observedAt, i18n.language, emptyValue)}` : t('pages.productDetail.channel.notFetched')}
                      </div>
                    </td>
                    <td style={{ padding: 'var(--sp-4)', verticalAlign: 'top' }}>
                      <StockObservation row={row} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

interface ListingStockPushPanelProps {
  accessToken: string
  productId: number
  channel: string
  connection?: ChannelConnection
  listing?: ProductChannelListing
  productSku?: string
  stockCapability?: ChannelStockPushCapability
  targetStockRow?: ProductChannelListingStock
  stockSource: SourceAuthority | null
  onProductRefresh: () => Promise<void>
}

function ListingStockPushPanel({ accessToken, productId, channel, connection, listing, productSku, stockCapability, targetStockRow, stockSource, onProductRefresh }: ListingStockPushPanelProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [open, setOpen] = useState(false)
  const [loadingStock, setLoadingStock] = useState(false)
  const [localStock, setLocalStock] = useState<ProductStock | null>(null)
  const [quantity, setQuantity] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [notice, setNotice] = useState<ChannelPushStockResult | null>(null)

  const targetExternalLocationId = nonBlank(targetStockRow?.externalLocationId)
  const targetLocationLabel = nonBlank(targetStockRow?.externalLocationName) ?? targetExternalLocationId ?? channel
  const stockPushEnabled = stockCapability?.enabled === true
  const noStockLocations = stockCapability?.locationRequired === true && targetExternalLocationId === null
  const targetConnectionId = connection?.id ?? listing?.channelConnectionId
  const sourceOwned = stockSource?.ownerType === 'CHANNEL_CONNECTION' && stockSource.channelConnectionId === targetConnectionId
  const isProductScopedPush = listing == null && connection?.provider === 'NOON'
  const warehouseCode = nonBlank(connection?.settings?.noon?.fbpiWarehouseCode)
  const localAvailable = localAvailableQuantity(localStock)
  const parsedQuantity = /^[0-9]+$/.test(quantity.trim()) ? Number(quantity.trim()) : null
  const minimumQuantity = stockCapability?.minimumQuantity ?? 0
  const maximumQuantity = stockCapability?.maximumQuantity ?? undefined
  const invalidQuantity = parsedQuantity == null
    || !Number.isSafeInteger(parsedQuantity)
    || parsedQuantity < minimumQuantity
    || (maximumQuantity !== undefined && parsedQuantity > maximumQuantity)
  const exceedsLocal = parsedQuantity != null && parsedQuantity > localAvailable
  const mobileActionStyle = mobileButtonStyle(isMobile)

  const openForm = async () => {
    setOpen(true)
    setLoadingStock(true)
    setError(null)
    setNotice(null)
    try {
      const stock = await getProductStock(accessToken, productId)
      const totalAvailable = localAvailableQuantity(stock)
      setLocalStock(stock)
      setQuantity(String(totalAvailable))
    } catch (caught) {
      setError(caught)
      setLocalStock(null)
      setQuantity('')
    } finally {
      setLoadingStock(false)
    }
  }

  const closeForm = () => {
    setOpen(false)
    setError(null)
    setNotice(null)
    setQuantity('')
    setLocalStock(null)
  }

  const submit = async () => {
    if (submitting) return
    if (invalidQuantity || parsedQuantity == null) {
      setError(new ApiClientError(400, { code: 'CHANNEL_STOCK_PUSH_INVALID_QUANTITY' }))
      return
    }

    setSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      const result = isProductScopedPush && connection
        ? await pushChannelProductStock(accessToken, connection.id, productId, { availableQuantity: parsedQuantity })
        : await pushChannelListingStock(accessToken, productId, listing!.id, {
            availableQuantity: parsedQuantity,
            ...(targetExternalLocationId ? { targetExternalLocationId } : {}),
          })
      setNotice(result)
      if (result.pushed > 0 && !isProductScopedPush) {
        await onProductRefresh()
      }
    } catch (caught) {
      setError(caught)
    } finally {
      setSubmitting(false)
    }
  }

  if (!stockPushEnabled) {
    return (
      <div style={{ marginTop: 18, padding: 14, borderRadius: 10, background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 12.5, lineHeight: 1.5, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Icon size={15} color="var(--fg-subtle)">{I.clock}</Icon>
        <div>{t(`pages.productDetail.channel.stockPush.capabilityReasons.${stockCapability?.reasonCode ?? 'CHANNEL_OPERATION_NOT_IMPLEMENTED'}`, {
          channel,
          defaultValue: t('pages.productDetail.channel.stockPush.unsupportedProvider', { channel }),
        })}</div>
      </div>
    )
  }

  if (sourceOwned) {
    return (
      <div style={{ marginTop: 18, padding: 14, borderRadius: 10, border: '1px solid var(--warning-100)', background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 12.5, lineHeight: 1.5, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Icon size={15} color="var(--warning)">{I.shield}</Icon>
        <div>{t('pages.productDetail.channel.stockPush.sourceOwned', { channel })}</div>
      </div>
    )
  }

  if (noStockLocations) {
    return (
      <div style={{ marginTop: 18, padding: 14, borderRadius: 10, border: '1px dashed var(--border-default)', background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 12.5, lineHeight: 1.5, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Icon size={15} color="var(--fg-subtle)">{I.alert}</Icon>
        <div>{t('pages.productDetail.channel.stockPush.noStockLocations', { channel })}</div>
      </div>
    )
  }

  const item = notice ? channelStockPushResultItem(notice) : null
  const noticeTone = notice?.failed ? 'danger' : notice?.skipped ? 'warning' : 'success'
  const noticeText = notice && (
    notice.pushed > 0
      ? t('pages.productDetail.channel.stockPush.success', { channel, quantity: (item?.availableQuantity ?? parsedQuantity ?? 0).toLocaleString(i18n.language) })
      : notice.failed > 0
        ? t(`pages.productDetail.channel.stockPush.itemReasons.${item?.reason ?? 'UNEXPECTED_ERROR'}`)
        : t(`pages.productDetail.channel.stockPush.itemReasons.${item?.reason ?? 'NO_STOCK_LOCATIONS'}`)
  )

  return (
    <div style={{ marginTop: isMobile ? 'var(--sp-2)' : 18, padding: isMobile ? 0 : 'var(--sp-4)', borderRadius: 'var(--r-lg)', border: isMobile ? undefined : '1px solid var(--border-subtle)', background: isMobile ? undefined : 'var(--ink-50)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) auto', gap: 12, alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--brand-900)' }}>
            {targetExternalLocationId
              ? t('pages.productDetail.channel.stockPush.titleForBranch', { location: targetLocationLabel })
              : t('pages.productDetail.channel.stockPush.title')}
          </div>
          <div style={{ marginTop: 3, fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.45 }}>
            {isProductScopedPush
              ? t('pages.productDetail.channel.stockPush.noonBody')
              : targetExternalLocationId
                ? t('pages.productDetail.channel.stockPush.bodyForBranch', { channel, location: targetLocationLabel })
                : t('pages.productDetail.channel.stockPush.body', { channel })}
          </div>
          {listing?.externalVariantId && <div style={{ marginTop: 4, fontSize: 12, color: 'var(--fg-muted)', ...TEXT_WRAP_STYLE }}>
            {t('pages.productDetail.channel.stockPush.variantScope', { title: listing.title ?? listing.sku, variantId: listing.externalVariantId })}
          </div>}
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--fg-muted)' }}>{t('pages.productDetail.channel.stockPush.absoluteTarget')}</div>
          {isProductScopedPush && (
            <div style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap', color: 'var(--fg-muted)', fontSize: 12 }}>
              <span>{t('pages.productDetail.channel.stockPush.noonSku')} <strong style={{ color: 'var(--fg)', fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere' }}>{productSku}</strong></span>
              <span>{t('pages.productDetail.channel.stockPush.noonWarehouse')} <strong style={{ color: 'var(--fg)', fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere' }}>{warehouseCode}</strong></span>
            </div>
          )}
        </div>
        {!open && (
          <Button kind="primary" size="xs" icon={I.zap} onClick={openForm} style={mobileActionStyle}>
            {targetExternalLocationId
              ? t('pages.productDetail.channel.stockPush.actionForBranch', { location: targetLocationLabel })
              : t('pages.productDetail.channel.stockPush.action')}
          </Button>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
          {loadingStock ? (
            <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon size={14}>{I.refresh}</Icon>
              {t('pages.productDetail.channel.stockPush.loadingStock')}
            </div>
          ) : error !== null && localStock === null ? (
            <div style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--danger-100)', color: 'var(--danger)', fontSize: 12.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon size={14}>{I.alert}</Icon>
              {String(t(channelStockPushErrorMessageKey(error), { defaultValue: String(t('pages.productDetail.channel.stockPush.errors.REQUEST_FAILED')) }))}
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 180px) minmax(0, 1fr)', gap: 10, alignItems: 'end' }}>
                <label style={{ display: 'grid', gap: 5, fontSize: 12, fontWeight: 800, color: 'var(--fg-subtle)' }}>
                  {t('pages.productDetail.channel.stockPush.quantityLabel')}
                  <input
                    aria-label={String(t('pages.productDetail.channel.stockPush.quantityLabel'))}
                    type="number"
                    min={minimumQuantity}
                    max={maximumQuantity}
                    step={1}
                    value={quantity}
                    onChange={event => {
                      setQuantity(event.target.value)
                      setError(null)
                      setNotice(null)
                    }}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '8px 10px',
                      fontSize: 13,
                      fontFamily: 'inherit',
                      color: 'var(--fg)',
                      background: 'white',
                      border: '1px solid var(--border-default)',
                      borderRadius: 8,
                      outline: 'none',
                      textAlign: 'end',
                    }}
                  />
                </label>
                <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.45 }}>
                  {t('pages.productDetail.channel.stockPush.localAvailable', { quantity: localAvailable.toLocaleString(i18n.language) })}
                </div>
              </div>

              {exceedsLocal && (
                <div style={{ padding: '9px 11px', borderRadius: 10, border: '1px solid var(--warning-100)', color: 'var(--warning)', background: 'white', fontSize: 12.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon size={14}>{I.alert}</Icon>
                  {t('pages.productDetail.channel.stockPush.exceedsLocalWarning')}
                </div>
              )}

              {error !== null && (
                <div style={{ padding: '9px 11px', borderRadius: 10, border: '1px solid var(--danger-100)', color: 'var(--danger)', background: 'white', fontSize: 12.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon size={14}>{I.alert}</Icon>
                  {String(t(channelStockPushErrorMessageKey(error), { defaultValue: String(t('pages.productDetail.channel.stockPush.errors.REQUEST_FAILED')) }))}
                </div>
              )}

              {noticeText && (
                <div style={{ padding: '9px 11px', borderRadius: 10, border: noticeTone === 'success' ? '1px solid var(--success-100)' : noticeTone === 'warning' ? '1px solid var(--warning-100)' : '1px solid var(--danger-100)', color: noticeTone === 'success' ? 'var(--success)' : noticeTone === 'warning' ? 'var(--warning)' : 'var(--danger)', background: 'white', fontSize: 12.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon size={14}>{noticeTone === 'success' ? I.check : I.alert}</Icon>
                  {noticeText}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-start' }}>
                <Button kind="primary" size="xs" onClick={submit} disabled={submitting || invalidQuantity} style={mobileActionStyle}>
                  {submitting ? t('pages.productDetail.channel.stockPush.submitting') : invalidQuantity
                    ? t('pages.productDetail.channel.stockPush.submit')
                    : t(listing?.externalVariantId ? 'pages.productDetail.channel.stockPush.setOptionQuantity' : 'pages.productDetail.channel.stockPush.setListingQuantity', { quantity: parsedQuantity!.toLocaleString(i18n.language) })}
                </Button>
                <Button kind="secondary" size="xs" onClick={closeForm} disabled={submitting} style={mobileActionStyle}>
                  {t('pages.productDetail.channel.stockPush.cancel')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function ChannelListingCard({
  accessToken,
  productId,
  channel,
  providerName,
  listing,
  connection,
  stockSource,
  onProductRefresh,
}: {
  accessToken: string
  productId: number
  channel: string
  providerName: string
  listing: ProductChannelListing
  connection?: ChannelConnection
  stockSource: SourceAuthority | null
  onProductRefresh: () => Promise<void>
}) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [refreshing, setRefreshing] = useState(false)
  const [refreshFailed, setRefreshFailed] = useState(false)
  const emptyValue = String(t('pages.productDetail.emptyValue'))
  const title = nonBlank(listing.title) ?? nonBlank(listing.externalProductId) ?? t('pages.productDetail.channel.listing', { channel })
  const externalVariantId = nonBlank(listing.externalVariantId)
  const stockSourceOwned = stockSource?.ownerType === 'CHANNEL_CONNECTION' && stockSource.channelConnectionId === listing.channelConnectionId
  const locationRows = connection?.capabilities?.stockPush.locationRequired && !stockSourceOwned
    ? sallaPushableBranchRows(listing)
    : []

  const refresh = async () => {
    if (refreshing) return
    setRefreshing(true)
    setRefreshFailed(false)
    try {
      if (listing.provider === 'NOON') await refreshNoonListing(accessToken, listing.channelConnectionId, listing.id)
      else await refreshChannelListingStock(accessToken, listing.channelConnectionId, listing.id)
      await onProductRefresh()
    } catch {
      setRefreshFailed(true)
    } finally {
      setRefreshing(false)
    }
  }

  const renderBranchPush = (row: ProductChannelListingStock) => locationRows.includes(row) ? (
    <ListingStockPushPanel
      key={row.id ?? row.externalStockId ?? row.externalLocationId}
      accessToken={accessToken}
      productId={productId}
      channel={channel}
      connection={connection}
      listing={listing}
      stockCapability={connection?.capabilities?.stockPush}
      targetStockRow={row}
      stockSource={stockSource}
      onProductRefresh={onProductRefresh}
    />
  ) : null

  return (
    <div style={{ padding: 18, border: '1px solid var(--border-subtle)', borderRadius: 12, background: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <ChannelDot name={providerName} size={32} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: 'var(--brand-900)', fontSize: 15, fontWeight: 800 }}>{title}</div>
          <div style={{ color: 'var(--fg-subtle)', fontSize: 12, marginTop: 2 }}>{t('pages.productDetail.channel.mappedSubtitle', { channel })}</div>
        </div>
        {listing.status && <Pill tone="info" size="sm">{listing.status}</Pill>}
        {['NOON', 'SALLA', 'ZID'].includes(listing.provider) && connection?.status === 'CONNECTED' && connection.adapterAvailable && (
          <Button kind="secondary" size="sm" icon={I.refresh} onClick={refresh} disabled={refreshing} style={mobileButtonStyle(isMobile)}>
            {t(refreshing ? 'pages.productDetail.channel.noonRefreshing' : 'pages.productDetail.channel.noonRefresh')}
          </Button>
        )}
      </div>
      {refreshFailed && <p role="alert" style={{ color: 'var(--danger)' }}>{t('pages.productDetail.channel.noonRefreshFailed')}</p>}

      <FieldRow label={t('pages.productDetail.channel.fields.title')} value={nonBlank(listing.title) ?? emptyValue} />
      <FieldRow label={t('pages.productDetail.channel.fields.status')} value={nonBlank(listing.status) ?? emptyValue} />
      <FieldRow label={t('pages.productDetail.channel.fields.sku')} mono value={nonBlank(listing.sku) ?? emptyValue} />
      <FieldRow label={t('pages.productDetail.channel.fields.externalProductId')} mono value={nonBlank(listing.externalProductId) ?? emptyValue} />
      {externalVariantId && <FieldRow label={t('pages.productDetail.channel.fields.externalVariantId')} mono value={externalVariantId} />}
      <FieldRow label={t('pages.productDetail.channel.fields.lastSeen')} value={formatOptionalDate(listing.lastSeenAt, i18n.language, emptyValue)} />
      <FieldRow label={t('pages.productDetail.channel.fields.lastStockPushed')} value={formatOptionalDate(listing.lastStockPushedAt, i18n.language, emptyValue)} />

      <ListingStockRows channel={channel} listing={listing} refreshing={refreshing} renderBranchPush={renderBranchPush}
        onRefresh={['NOON', 'SALLA', 'ZID'].includes(listing.provider) && connection?.status === 'CONNECTED' && connection.adapterAvailable ? refresh : undefined} />
      {connection?.provider !== 'NOON' && (locationRows.length > 0 ? (
        !isMobile && <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>{locationRows.map(renderBranchPush)}</div>
      ) : (
        <ListingStockPushPanel
          accessToken={accessToken}
          productId={productId}
          channel={channel}
          connection={connection}
          listing={listing}
          stockCapability={connection?.capabilities?.stockPush}
          stockSource={stockSource}
          onProductRefresh={onProductRefresh}
        />
      ))}
    </div>
  )
}

function ChannelTab({
  accessToken,
  productId,
  channel,
  providerName,
  channelConnectionId,
  connection,
  productSku,
  listings,
  stockSource,
  onProductRefresh,
}: {
  accessToken: string
  productId: number
  channel: string
  providerName: string
  channelConnectionId: number
  connection?: ChannelConnection
  productSku: string
  listings: ProductChannelListing[]
  stockSource: SourceAuthority | null
  onProductRefresh: () => Promise<void>
}) {
  const { t } = useTranslation()
  const channelListings = listings.filter(listing => listing.channelConnectionId === channelConnectionId)
  const noonStockPushPanel = connection?.provider === 'NOON' ? (
    <ListingStockPushPanel
      accessToken={accessToken}
      productId={productId}
      channel={channel}
      connection={connection}
      productSku={productSku}
      stockCapability={connection.capabilities?.stockPush}
      stockSource={stockSource}
      onProductRefresh={onProductRefresh}
    />
  ) : null

  if (channelListings.length === 0) {
    return (
      <Card padding={28} style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <ChannelDot name={providerName} size={36} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-900)' }}>{t('pages.productDetail.channel.listing', { channel })}</div>
            <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{t('pages.productDetail.channel.notListedSubtitle', { channel })}</div>
          </div>
          <Pill tone="neutral" size="sm">{t('pages.productDetail.channel.notListed')}</Pill>
        </div>

        <div style={{ padding: 24, border: '1px dashed var(--border-default)', borderRadius: 12, background: 'var(--ink-50)' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <Icon size={17} color="var(--brand-600)">{I.link}</Icon>
            <div>
              <div style={{ color: 'var(--brand-900)', fontSize: 14, fontWeight: 800 }}>{t('pages.productDetail.channel.notListedTitle', { channel })}</div>
              <div style={{ color: 'var(--fg-muted)', fontSize: 13, marginTop: 4, lineHeight: 1.55 }}>{t(connection?.provider === 'NOON' ? 'pages.productDetail.channel.noonNotListedBody' : 'pages.productDetail.channel.notListedBody', { channel })}</div>
            </div>
          </div>
        </div>
        {noonStockPushPanel}
      </Card>
    )
  }

  return (
    <Card padding={28} style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <ChannelDot name={providerName} size={36} />
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-900)' }}>{t('pages.productDetail.channel.listing', { channel })}</div>
          <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{t('pages.productDetail.channel.mappedSubtitle', { channel })}</div>
        </div>
        <Pill tone="success" size="sm">{t(channelListings.length === 1 ? 'pages.productDetail.channel.mappedCount' : 'pages.productDetail.channel.mappedCount_plural', { count: channelListings.length })}</Pill>
      </div>

      <div style={{ display: 'grid', gap: 14 }}>
        {channelListings.map(listing => (
          <ChannelListingCard
            key={listing.id}
            accessToken={accessToken}
            productId={productId}
            channel={channel}
            providerName={providerName}
            listing={listing}
            connection={connection}
            stockSource={stockSource}
            onProductRefresh={onProductRefresh}
          />
        ))}
      </div>
      {noonStockPushPanel}
    </Card>
  )
}

interface ProductDetailPageProps {
  accessToken: string
  productId: number
  onBack?: () => void
  onEdit?: (productId: number) => void
  singleItemImportNotice?: InventorySingleItemImportResponse | null
  onDismissSingleItemImportNotice?: () => void
}

function groupedSingleImportIssues(notice: InventorySingleItemImportResponse) {
  const counts = new Map<string, number>()
  for (const issue of notice.result.issues) {
    counts.set(issue.code, (counts.get(issue.code) ?? 0) + 1)
  }
  return Array.from(counts, ([code, count]) => ({ code, count }))
}

export function ProductDetailPage({
  accessToken,
  productId,
  onBack,
  onEdit,
  singleItemImportNotice = null,
  onDismissSingleItemImportNotice,
}: ProductDetailPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [activeTab, setActiveTab] = useState('master')
  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)
  const [catalogSource, setCatalogSource] = useState<SourceAuthority | null>(null)
  const [stockSource, setStockSource] = useState<SourceAuthority | null>(null)
  const [inventoryConnections, setInventoryConnections] = useState<InventoryConnection[]>([])
  const [channelConnections, setChannelConnections] = useState<ChannelConnection[]>([])
  const [refreshingZoho, setRefreshingZoho] = useState(false)
  const [refreshError, setRefreshError] = useState<unknown>(null)
  const [refreshNotice, setRefreshNotice] = useState<{ issuesCount: number } | null>(null)

  useEffect(() => {
    let cancelled = false
    // Intentional reset on productId change so the page re-enters its loading
    // state instead of briefly showing the previous product. The set-state-in-effect
    // rule's general guidance doesn't fit this canonical fetch-on-deps pattern.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setError(null)
    setRefreshError(null)
    setRefreshNotice(null)
    getProduct(accessToken, productId)
      .then(response => {
        if (!cancelled) setProduct(response)
      })
      .catch(caught => {
        if (!cancelled) setError(caught)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, productId])

  useEffect(() => {
    let cancelled = false
    Promise.allSettled([
      getSourceAuthority(accessToken, 'PRODUCT_CATALOG'),
      getSourceAuthority(accessToken, 'STOCK'),
      listInventoryConnections(accessToken),
      listChannelConnections(accessToken),
    ]).then(([sourceResult, stockSourceResult, inventoryConnectionsResult, channelConnectionsResult]) => {
      if (cancelled) return
      setCatalogSource(sourceResult.status === 'fulfilled' ? sourceResult.value : null)
      setStockSource(stockSourceResult.status === 'fulfilled' ? stockSourceResult.value : null)
      setInventoryConnections(inventoryConnectionsResult.status === 'fulfilled' && Array.isArray(inventoryConnectionsResult.value) ? inventoryConnectionsResult.value : [])
      setChannelConnections(channelConnectionsResult.status === 'fulfilled' && Array.isArray(channelConnectionsResult.value) ? channelConnectionsResult.value : [])
    })
    return () => {
      cancelled = true
    }
  }, [accessToken])

  const zohoCatalogConnection = inventoryConnections.find(connection => (
    connection.provider === 'ZOHO'
    && connection.status === 'CONNECTED'
    && catalogSource?.ownerType === 'INVENTORY_CONNECTION'
    && catalogSource.inventoryConnectionId === connection.id
  ))

  const refreshFromZoho = async () => {
    if (!product || refreshingZoho) return
    setRefreshingZoho(true)
    setRefreshError(null)
    setRefreshNotice(null)
    try {
      const response = await refreshProductFromZoho(accessToken, product.id)
      setProduct(response.product)
      setRefreshNotice({ issuesCount: response.result.issues.length })
    } catch (caught) {
      setRefreshError(caught)
    } finally {
      setRefreshingZoho(false)
    }
  }

  const refetchProduct = async () => {
    const response = await getProduct(accessToken, productId)
    setProduct(response)
  }

  const productChannelListings = product?.channelListings ?? []
  const connectedChannelConnections = channelConnections.filter(connection => connection.status === 'CONNECTED')
  const tabConnectionIds = new Set<number>()
  const channelTabs: ProductDetailTab[] = connectedChannelConnections.map(connection => {
    tabConnectionIds.add(connection.id)
    return {
      kind: 'channel',
      id: `channel:${connection.id}`,
      label: connection.displayName || channelDisplayName(connection.provider),
      providerName: channelDisplayName(connection.provider),
      channelConnectionId: connection.id,
      count: productChannelListings.filter(listing => listing.channelConnectionId === connection.id).length,
    }
  })

  for (const listing of productChannelListings) {
    if (tabConnectionIds.has(listing.channelConnectionId)) continue
    tabConnectionIds.add(listing.channelConnectionId)
    const providerName = channelDisplayName(listing.provider)
    channelTabs.push({
      kind: 'channel',
      id: `channel:${listing.channelConnectionId}`,
      label: providerName,
      providerName,
      channelConnectionId: listing.channelConnectionId,
      count: productChannelListings.filter(row => row.channelConnectionId === listing.channelConnectionId).length,
    })
  }

  const tabs: ProductDetailTab[] = [
    { kind: 'master', id: 'master', label: t('pages.productDetail.tabs.master'), count: 0 },
    ...channelTabs,
  ]
  const activeChannelTab = tabs.find((tab): tab is Extract<ProductDetailTab, { kind: 'channel' }> => tab.kind === 'channel' && tab.id === activeTab)

  if (loading) {
    return <Card padding={22}><div style={{ color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700 }}>{t('pages.productDetail.loading')}</div></Card>
  }

  if (error || !product) {
    return (
      <Card padding={22} style={{ borderColor: 'var(--danger-100)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700 }}>
          <Icon size={16}>{I.alert}</Icon>
          {String(t(errorMessageKey(error), { defaultValue: String(t('pages.products.errors.codes.REQUEST_FAILED')) }))}
        </div>
        <Button kind="secondary" size="sm" onClick={onBack} style={{ marginTop: 14 }}>{t('pages.productDetail.actions.back')}</Button>
      </Card>
    )
  }

  const master = product.images.find(image => image.primary) ?? product.images[0]
  const visibleSingleItemImportNotice = singleItemImportNotice?.product.id === productId ? singleItemImportNotice : null
  const singleImportIssueGroups = visibleSingleItemImportNotice ? groupedSingleImportIssues(visibleSingleItemImportNotice) : []

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 13, color: 'var(--fg-subtle)', marginBottom: 14 }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--fg-muted)', fontFamily: 'inherit', fontSize: 13 }}>{t('pages.productDetail.breadcrumbProducts')}</button>
        <span>{i18n.dir() === 'rtl' ? '‹' : '›'}</span>
        <span style={{ color: 'var(--fg)' }}>{product.name}</span>
      </div>

      {product.optionsReleasedAt && <OptionsReleasedNotice product={product} accessToken={accessToken} onProductChange={setProduct} />}

      {visibleSingleItemImportNotice && (
        <Card padding={isMobile ? 16 : 20} style={{ marginBottom: 14, borderColor: 'var(--success-100)', background: 'var(--ink-50)' }}>
          <div role="status" style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'minmax(0, 1fr) auto', gap: 10, alignItems: 'start' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--success)' }}>
                  <Icon size={16}>{I.check}</Icon>
                  <strong style={{ fontSize: 14.5 }}>{t(`pages.productDetail.singleImportNotice.outcomes.${visibleSingleItemImportNotice.outcome}`)}</strong>
                </div>
                <div style={{ color: 'var(--fg-muted)', fontSize: 12.5, marginTop: 5, lineHeight: 1.5, ...TEXT_WRAP_STYLE }}>
                  {t('pages.productDetail.singleImportNotice.subtitle')}
                </div>
              </div>
              <Button kind="ghost" size="sm" onClick={onDismissSingleItemImportNotice} style={mobileButtonStyle(isMobile)}>
                {t('pages.productDetail.singleImportNotice.dismiss')}
              </Button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
              <div style={{ minWidth: 0, padding: '10px 12px', borderRadius: 10, background: 'white', border: '1px solid var(--border-subtle)' }}>
                <div style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 700 }}>{t('pages.productDetail.singleImportNotice.catalogLabel')}</div>
                <div style={{ color: 'var(--brand-900)', fontSize: 12.5, fontWeight: 700, marginTop: 3, ...TEXT_WRAP_STYLE }}>
                  {t(`pages.productDetail.singleImportNotice.catalog.${visibleSingleItemImportNotice.productMode}`)}
                </div>
              </div>
              <div style={{ minWidth: 0, padding: '10px 12px', borderRadius: 10, background: 'white', border: '1px solid var(--border-subtle)' }}>
                <div style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 700 }}>{t('pages.productDetail.singleImportNotice.stockLabel')}</div>
                <div style={{ color: 'var(--brand-900)', fontSize: 16, fontWeight: 800, marginTop: 3 }}>{visibleSingleItemImportNotice.result.stockRowsImported.toLocaleString(i18n.language)}</div>
              </div>
              <div style={{ minWidth: 0, padding: '10px 12px', borderRadius: 10, background: 'white', border: '1px solid var(--border-subtle)' }}>
                <div style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 700 }}>{t('pages.productDetail.singleImportNotice.warehousesLabel')}</div>
                <div style={{ color: 'var(--brand-900)', fontSize: 16, fontWeight: 800, marginTop: 3 }}>{visibleSingleItemImportNotice.result.warehousesCreated.toLocaleString(i18n.language)}</div>
              </div>
              <div style={{ minWidth: 0, padding: '10px 12px', borderRadius: 10, background: 'white', border: '1px solid var(--border-subtle)' }}>
                <div style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 700 }}>{t('pages.productDetail.singleImportNotice.issuesLabel')}</div>
                <div style={{ color: visibleSingleItemImportNotice.result.issues.length > 0 ? 'var(--warning)' : 'var(--brand-900)', fontSize: 16, fontWeight: 800, marginTop: 3 }}>{visibleSingleItemImportNotice.result.issues.length.toLocaleString(i18n.language)}</div>
              </div>
            </div>

            {singleImportIssueGroups.length > 0 && (
              <div style={{ display: 'grid', gap: 8 }}>
                <strong style={{ color: 'var(--brand-900)', fontSize: 13 }}>{t('pages.productDetail.singleImportNotice.issueDetails')}</strong>
                {singleImportIssueGroups.map(group => (
                  <div key={group.code} style={{ padding: '10px 12px', borderRadius: 10, background: 'white', border: '1px solid var(--border-subtle)', minWidth: 0 }}>
                    <div style={{ color: 'var(--warning)', fontSize: 12.5, fontWeight: 800, ...TEXT_WRAP_STYLE }}>
                      {t(`pages.inventoryZohoPullRunDetail.issueCodes.${group.code}.title`, {
                        defaultValue: t('pages.inventoryZohoPullRunDetail.issueCodes.UNKNOWN.title'),
                      })}
                      {' · '}
                      {t('pages.productDetail.singleImportNotice.issueCount', { count: group.count })}
                    </div>
                    <div style={{ color: 'var(--fg-muted)', fontSize: 12, lineHeight: 1.5, marginTop: 3, ...TEXT_WRAP_STYLE }}>
                      {t(`pages.inventoryZohoPullRunDetail.issueCodes.${group.code}.action`, {
                        defaultValue: t('pages.inventoryZohoPullRunDetail.issueCodes.UNKNOWN.action'),
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      <Card padding={isMobile ? 18 : 24} style={{ marginBottom: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: 'none' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'auto minmax(0, 1fr) auto', gap: isMobile ? 16 : 20, alignItems: 'center' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '64px minmax(0, 1fr)' : 'auto', gap: isMobile ? 14 : 0, alignItems: 'center' }}>
            {master?.publicUrl
              ? <img src={master.publicUrl} alt={master.altText ?? product.name} style={{ width: isMobile ? 64 : 72, height: isMobile ? 64 : 72, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
              : <Thumb name={product.name} size={isMobile ? 64 : 72} hue={product.id % 8} />}
            {isMobile && (
              <div style={{ minWidth: 0 }}>
                <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                  <Pill tone={statusTone(product.status)} size="sm">{t(`pages.products.status.${product.status}`)}</Pill>
                  {(product.optionCount ?? 0) > 0 && <Pill tone="brand" size="sm">{t('pages.products.options.count', { count: product.optionCount })}</Pill>}
                </span>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--brand-900)', margin: '8px 0 0', letterSpacing: 0, lineHeight: 1.2, overflowWrap: 'break-word' }}>{product.name}</h2>
              </div>
            )}
          </div>
          {!isMobile && (
            <div style={{ minWidth: 0 }}>
              <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                <Pill tone={statusTone(product.status)} size="sm">{t(`pages.products.status.${product.status}`)}</Pill>
                {(product.optionCount ?? 0) > 0 && <Pill tone="brand" size="sm">{t('pages.products.options.count', { count: product.optionCount })}</Pill>}
              </span>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-900)', margin: '8px 0 0', letterSpacing: 0, lineHeight: 1.2 }}>{product.name}</h2>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
            {zohoCatalogConnection && (
              <Button kind="primary" size="sm" icon={I.refresh} onClick={refreshFromZoho} disabled={refreshingZoho}>
                {refreshingZoho ? t('pages.productDetail.zohoRefresh.refreshing') : t('pages.productDetail.zohoRefresh.action')}
              </Button>
            )}
            <Button kind="secondary" size="sm" icon={I.edit} onClick={() => onEdit?.(product.id)}>{t('pages.productDetail.actions.edit')}</Button>
          </div>
          <div style={{ gridColumn: isMobile ? '1' : '2 / 4', display: 'flex', gap: isMobile ? 8 : 16, marginTop: isMobile ? 0 : -10, fontSize: 12.5, color: 'var(--fg-muted)', flexWrap: 'wrap' }}>
            {product.generatedSku
              ? <span style={{ color: 'var(--fg-muted)' }}>{t('pages.products.options.noSku')}</span>
              : <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg)', overflowWrap: 'anywhere' }}>{product.sku}</span>}
            <span><strong style={{ color: 'var(--fg)' }}>{t('pages.productDetail.price', { value: formatMoney(product.price, i18n.language) })}</strong></span>
            <span>{t('pages.productDetail.imageCount', { count: product.images.length })}</span>
            <span>{t('pages.productDetail.lastUpdated')} <strong style={{ color: 'var(--fg)' }}>{formatDate(product.updatedAt, i18n.language)}</strong></span>
          </div>
        </div>
        {refreshNotice !== null && (
          <div style={{ marginTop: 16, padding: '11px 14px', borderRadius: 10, border: '1px solid var(--success-100)', color: 'var(--success)', background: 'var(--ink-50)', fontSize: 12.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon size={15}>{I.check}</Icon>
            {refreshNotice.issuesCount > 0
              ? t('pages.productDetail.zohoRefresh.successWithIssues', { count: refreshNotice.issuesCount })
              : t('pages.productDetail.zohoRefresh.success')}
          </div>
        )}
        {refreshError !== null && (
          <div style={{ marginTop: 16, padding: '11px 14px', borderRadius: 10, border: '1px solid var(--danger-100)', color: 'var(--danger)', fontSize: 12.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon size={15}>{I.alert}</Icon>
            {String(t(zohoRefreshErrorMessageKey(refreshError), { defaultValue: String(t('pages.productDetail.zohoRefresh.errors.REQUEST_FAILED')) }))}
          </div>
        )}
      </Card>

      <Card padding={0} style={{ borderRadius: 0, borderTop: 'none', borderBottom: 'none' }}>
        <div style={{ display: 'flex', gap: 0, padding: '0 20px', background: 'var(--ink-50)', borderTop: '1px solid var(--border-subtle)', overflowX: 'auto' }}>
          {tabs.map(tab => {
            const isActive = tab.id === activeTab
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                padding: '13px 16px', background: 'transparent', border: 'none',
                borderBottom: isActive ? '2px solid var(--brand-500)' : '2px solid transparent',
                color: isActive ? 'var(--brand-900)' : 'var(--fg-muted)',
                fontWeight: isActive ? 700 : 600, fontSize: 13, fontFamily: 'inherit',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
              }}>
                {tab.kind === 'channel' && <ChannelDot name={tab.providerName} size={18} />}
                {tab.kind === 'master' && <Icon size={14}>{I.shield}</Icon>}
                {tab.label}
                {tab.count > 0 && (
                  <span style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: 'var(--brand-100)', color: 'var(--brand-700)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>
                    {tab.count.toLocaleString(i18n.language)}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </Card>

      {activeChannelTab == null
        ? <MasterTab product={product} accessToken={accessToken} stockSource={stockSource} catalogSource={catalogSource} />
        : (
          <ChannelTab
            accessToken={accessToken}
            productId={product.id}
            channel={activeChannelTab.label}
            providerName={activeChannelTab.providerName}
            channelConnectionId={activeChannelTab.channelConnectionId}
            connection={channelConnections.find(connection => connection.id === activeChannelTab.channelConnectionId)}
            productSku={product.sku}
            listings={product.channelListings ?? []}
            stockSource={stockSource}
            onProductRefresh={refetchProduct}
          />
        )}
    </div>
  )
}
